import Image from "next/image";
import { sbBlok } from "@/types/sb-types";
import Cta from "@/components/Cta";
import clsx from "clsx";
import { formatArticleDate } from "@/utils/articles";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";

interface BlogPostProps {
  isLarge?: boolean;
  ctaType?: string;
  blok?: sbBlok;
  publishedAt?: string;
}

const BlogPost = ({ isLarge, ctaType, blok, publishedAt }: BlogPostProps) => {
  const cta = blok?.cta_link && blok?.cta_link[0];
  const date = formatArticleDate(publishedAt);

  return (
    <div
      className={clsx("flex w-full flex-col overflow-hidden z-1", {
        // Small card: image on top, dark text box below
        "rounded-[10px] bg-tm-c-gray-900": !isLarge,
        // Large card: stacked on mobile/tablet, side by side on desktop
        "gap-7.5 lg:flex-row lg:items-center": isLarge,
      })}
      style={{ cursor: isLarge ? "default" : "pointer" }}
    >
      <div
        className={clsx("relative w-full aspect-video", {
          "rounded-[10px] overflow-hidden lg:flex-1 lg:min-w-0": isLarge,
        })}
      >
        {blok?.image?.filename && (
          <Image
            src={blok.image.filename}
            alt={blok.image.alt || ""}
            fill
            sizes={
              isLarge
                ? "(min-width: 960px) 66vw, 100vw"
                : "(min-width: 960px) 33vw, (min-width: 800px) 50vw, 100vw"
            }
            className="object-cover"
            quality={100}
          />
        )}
      </div>

      <div
        className={clsx("flex flex-col", {
          "gap-4 px-5 py-4 grow": !isLarge,
          "gap-6 lg:w-[40%] 2xl:w-125 lg:shrink-0 2xl:gap-12.5": isLarge,
        })}
      >
        <div
          className={clsx("flex flex-col", {
            "gap-4 grow": !isLarge,
            "gap-6 2xl:gap-5": isLarge,
          })}
        >
          <div className="flex flex-col gap-2">
            {blok?.meta_title && (
              <h3 className="truncate text-xs! text-tm-c-gray-300 2xl:text-base!">
                {blok.meta_title}
              </h3>
            )}
            <h2
              className={clsx("m-0", {
                "line-clamp-2 min-h-[2lh] text-base! 2xl:text-xl!": !isLarge,
                "text-2xl! 2xl:text-4xl!": isLarge,
              })}
            >
              {blok?.title}
            </h2>
            {isLarge && blok?.intro && (
              <p className="text-base 2xl:text-xl">{blok.intro}</p>
            )}
          </div>

          {date && (
            <div
              className={clsx("flex items-center gap-1 text-tm-c-gray-300", {
                "mt-auto justify-end text-[10px] 2xl:text-xs": !isLarge,
                "text-xs": isLarge,
              })}
            >
              <CalendarMonthOutlinedIcon sx={{ fontSize: 16 }} />
              <span>{date}</span>
            </div>
          )}
        </div>

        {isLarge && cta && ctaType === "button" && (
          <Cta
            href={cta.link.url}
            fullSlug={cta.link?.story?.full_slug}
            name={cta.name}
          />
        )}
      </div>
    </div>
  );
};

export default BlogPost;
